<template>
  <div id="app">
    <LandingPage
      v-if="currentPage==='LandingPage'"
      @user-logged-in="handleGoogleSuccess"
    />
    <SignupPage
      v-if="currentPage === 'SignupPage'"
      :initialFullname="googleFullname"
      :initialEmail="googleEmail"
      :user="loggedInUser"
      @user-registered="handleUserRegistered"
      @change-page="changePage"
    />
    <KeepAlive>
      <DashboardPage
        v-if="currentPage === 'DashboardPage' && loggedInUser && loggedInUser.user_type"
        :key="dashboardKey"
        :user="loggedInUser"
        @change-page="changePage"
        @edit-pet="openEditPetPage"
        @view-pet-profile="viewPetProfile"
        @profile-updated="user = $event"
      />
    </KeepAlive>
    <AddPetPage 
      v-if="currentPage === 'AddPetPage'" 
      :user="currentUser" 
      @change-page="changePage" 
    />
    <LoginPage 
      v-if="currentPage === 'LoginPage'" 
      @change-page="changePage" 
      @user-logged-in="loggedInUser = $event"
    />
    <WelcomePage 
      v-if="currentPage === 'WelcomePage'" 
      :user="loggedInUser"
      @change-page="changePage" 
    />
    <LogoutPage
      v-if="currentPage === 'LogoutPage'"
      :user="loggedInUser"
      @change-page="changePage"
    />
    <EditPetDashboard 
      v-if="currentPage === 'EditPetDashboard'"
      :pet="selectedPet"
      :user="loggedInUser"
      @close="changePage('DashboardPage')"
      @changePage="changePage"
    />
    <BookPetAppointmentPage
      v-if="currentPage === 'BookPetAppointmentPage' && pageProps && pageProps.pet"
      :pet-id="pageProps.pet.id"
      :user="loggedInUser"
      @change-page="changePage"
      @close="changePage('DashboardPage')"
    />
    <MeetingsPage
      v-if="currentPage === 'MeetingsPage'"
      :user="loggedInUser"
      @change-page="changePage"
    />
    <CalendarPage
      v-if="currentPage === 'CalendarPage'"
      :user="loggedInUser"
      @change-page="changePage"
    />
    <EditUserDashboard
      v-if="currentPage === 'EditUserDashboard'"
      :user="loggedInUser"
      :pets="userPets"
      @change-page="changePage"
    />
    <PetDescriptionPage
      v-if="currentPage === 'PetDescriptionPage'"
      :pet="pageProps.pet"
      :user="loggedInUser"
      @change-page="changePage"
      @close="changePage('DashboardPage')"
    />
    <UsersFavouritesPage
      v-if="currentPage === 'UsersFavouritesPage'"
      :user="loggedInUser"
      @view-pet-profile="viewPetProfile"
      @change-page="changePage"
    />
    <YourPetsPage 
      v-if="currentPage === 'YourPetsPage'" 
      :user="loggedInUser"
      @change-page="changePage" 
      @edit-pet="openEditPetPage"
    />
    <div v-if="showPetPrompt" class="popup-overlay">
      <div class="popup-card">
        <div class="popup-icon">🐾</div>
        <h2 class="popup-title">Add Your Pet?</h2>
        <p class="popup-sub">You haven't added any pets yet. Would you like to add one so others can find them?</p>
        <div class="popup-btns">
          <button class="popup-btn popup-yes" @click="petPromptYes">Yes, let's go!</button>
          <button class="popup-btn popup-later" @click="petPromptLater">Remind me later</button>
          <button class="popup-btn popup-stop" @click="petPromptStop">Don't remind me again</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LandingPage from './components/LandingPage.vue';
import SignupPage from './components/SignupPage.vue';
import DashboardPage from './components/DashboardPage.vue';
import AddPetPage from './components/AddPetPage.vue';
import LoginPage from './components/LoginPage.vue';
import WelcomePage from './components/WelcomePage.vue';
import LogoutPage from './components/LogoutPage.vue';
import EditPetDashboard from './components/EditPetDashboard.vue';
import BookPetAppointmentPage from './components/BookPetAppointmentPage.vue';
import MeetingsPage from './components/MeetingsPage.vue';
import CalendarPage from './components/CalendarPage.vue';
import EditUserDashboard from './components/EditUserDashboard.vue';
import PetDescriptionPage from './components/PetDescriptionPage.vue';
import UsersFavouritesPage from './components/UsersFavouritesPage.vue';
import YourPetsPage from './components/YourPetsPage.vue';
import axios from 'axios';

const routeMap = {
  LandingPage:            '/',
  SignupPage:             '/signup',
  DashboardPage:          '/dashboard',
  AddPetPage:             '/add-pet',
  LoginPage:              '/login',
  WelcomePage:            '/welcome',
  LogoutPage:             '/logout',
  EditPetDashboard:       '/edit-pet',
  BookPetAppointmentPage: '/book',
  MeetingsPage:           '/meetings',
  CalendarPage:           '/calendar',
  EditUserDashboard:      '/account',
  PetDescriptionPage:     '/pet',
  UsersFavouritesPage:    '/favourites',
  YourPetsPage:           '/my-pets',
};

export default {
  components: {
    LandingPage,
    SignupPage,
    DashboardPage,
    AddPetPage,
    LoginPage,
    WelcomePage,
    LogoutPage,
    EditPetDashboard,
    BookPetAppointmentPage,
    MeetingsPage,
    CalendarPage,
    UsersFavouritesPage,
    EditUserDashboard,
    PetDescriptionPage,
    YourPetsPage
  },
  data() {
    return {
      currentPage: 'LandingPage',
      pageHistory: [],
      currentUser: null,
      selectedPet: null,
      loggedInUser: null,
      pageProps: {},
      userPets: [],
      googleFullname: '',
      googleEmail: '',
      editMode: false,
      dashboardKey: 0,
      showPetPrompt: false,
    };
  },
  methods: {
    viewPetProfile(pet) {
      this.changePage('PetDescriptionPage', { pet });
    },
    async handleGoogleSuccess(payload) {
      console.log('✅ Google login response:', payload);
      this.loggedInUser = payload;
      this.currentUser = payload;
      this.googleFullname = payload.fullname || '';
      this.googleEmail = payload.email || '';

      if (payload.isNew || !payload.user_type) {
        this.changePage('SignupPage');
      } else if (payload.user_type === 'Pet Owner') {
        // check if they've stopped reminders
        const stopped = localStorage.getItem(`stopPetReminder_${payload.id}`);
        if (!stopped) {
          try {
            const { data } = await axios.get(
              `${process.env.VUE_APP_API_URL}/api/pets/my-pets`,
              { withCredentials: true }
            );
            if (data.length === 0) {
              this.showPetPrompt = true;
              return; // don't navigate yet
            }
          } catch (e) {
            console.error('Could not check pets:', e);
          }
        }
        this.changePage('DashboardPage');
      } else {
        this.changePage('DashboardPage');
      }
    },
    onLogin(payload) {
      console.log('✅ onLogin payload:', payload);
      this.googleEmail =
        payload.email ||
        (payload.user && payload.user.email) ||
        '';
      this.googleFullname =
        payload.fullname ||
        (payload.user && payload.user.fullname) ||
        '';
      this.currentUser = payload;
      this.loggedInUser = payload;

      axios
        .get(`${process.env.VUE_APP_API_URL}/api/auth/users/email/${this.googleEmail}`, { withCredentials: true })
        .then(res => {
          this.loggedInUser = res.data.user;
          if (!this.loggedInUser?.user_type) {
            return this.changePage('SignupPage');
          }
          this.changePage('DashboardPage');
        })
        .catch(err => {
          console.error('Error fetching user by email:', err);
        });
    },
    changePage(page, props = {}) {
      this.pageHistory.push(this.currentPage);
      const path = routeMap[page] || '/';
      window.history.pushState({ page }, '', path);
      this.currentPage = page;
      this.pageProps = props;
      localStorage.setItem('currentPage', page);   // ← add this
      if (props.pet) this.selectedPet = props.pet;
      if (page === 'DashboardPage') this.dashboardKey++;
    },
    handleBackButton() {
      if (this.pageHistory.length > 0) {
        const previousPage = this.pageHistory.pop();
        const path = routeMap[previousPage] || '/';
        window.history.pushState({ page: previousPage }, '', path);
        this.currentPage = previousPage;
        this.pageProps = {};
      } else {
        window.history.back();
      }
    },
    setUser(user) {
      this.currentUser = user;
      this.currentPage = 'DashboardPage';
    },
    openEditPetPage(pet) {
      this.selectedPet = pet;
      this.changePage('EditPetDashboard');
    },
    closePetDashboard() {
      this.selectedPet = null;
    },
    handleUserRegistered(user) {
      console.log('✅ handleUserRegistered got user:', user);
      this.loggedInUser = user;
      this.changePage('DashboardPage');
    },
    petPromptYes() {
      this.showPetPrompt = false;
      this.changePage('AddPetPage');
    },
    petPromptLater() {
      this.showPetPrompt = false;
      this.changePage('DashboardPage');
    },
    petPromptStop() {
      localStorage.setItem(`stopPetReminder_${this.loggedInUser.id}`, 'true');
      this.showPetPrompt = false;
      this.changePage('DashboardPage');
    },
  },
  async mounted() {
    window.addEventListener('popstate', this.handleBackButton);

    const savedPage = localStorage.getItem('currentPage');
    const publicPages = ['LandingPage', 'LogoutPage', 'LoginPage', 'SignupPage'];

    if (savedPage && !publicPages.includes(savedPage)) {
      try {
        const { data } = await axios.get(
          `${process.env.VUE_APP_API_URL}/api/auth/me`,
          { withCredentials: true }
        );
        if (data && data.id) {
          this.loggedInUser = data;
          this.currentUser = data;
          this.currentPage = savedPage;
          const path = routeMap[savedPage] || '/';
          window.history.replaceState({ page: savedPage }, '', path);
          return;
        }
      } catch (e) {
        // not logged in, fall through to landing
      }
    }

    window.history.pushState({ page: this.currentPage }, '', routeMap[this.currentPage] || '/');
  },
  beforeUnmount() {
    window.removeEventListener('popstate', this.handleBackButton);
  }
};
</script>

<style>
.popup-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.popup-card {
  background: #fff;
  border-radius: 24px;
  border: 1px solid #f0e0ff;
  padding: 36px 28px;
  max-width: 360px;
  width: 100%;
  text-align: center;
  box-shadow: 0 8px 32px rgba(186, 113, 255, 0.15);
}

.popup-icon { font-size: 40px; margin-bottom: 12px; }

.popup-title {
  font-size: 20px;
  font-weight: 800;
  color: #1a1a1a;
  margin-bottom: 8px;
}

.popup-sub {
  font-size: 13px;
  color: #aaa;
  font-weight: 500;
  line-height: 1.6;
  margin-bottom: 24px;
}

.popup-btns {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.popup-btn {
  width: 100%;
  padding: 11px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.18s, transform 0.1s;
  font-family: inherit;
}

.popup-yes   { background: #eac7ff; color: #4a1070; }
.popup-yes:hover { background: #ba71ff; color: #fff; }

.popup-later { background: #f5f5f5; color: #888; border: 1.5px solid #e8e8e8; }
.popup-later:hover { background: #efefef; }

.popup-stop  { background: none; border: none; color: #ccc; font-size: 12px; font-weight: 600; cursor: pointer; padding: 4px; }
.popup-stop:hover { color: #999; }
</style>